/* The effort's edit form (designs/intake-ui-parity.md UP12, intake-data-
   model IDM4-a, one form since intake-unified-form.md UF1/UF13–UF16): the
   capture form's fields and idioms, initialized from the CURRENT record,
   PUT as a partial edit of exactly the changed fields — a no-op save mints
   nothing. Reachable by the Admin and by the ORIGINAL SUBMITTER; archiving
   rides along for Admins only (curation, not correction).

   Priority, phase target and Jama status are groom-side or not recorded:
   this form never touches them, and a legacy row's domain / area / detail
   block are read-only facts the whitelist drops. */
import { useEffect, useState, type JSX } from "react";
import {
  SCOPES,
  SUB_STATUSES,
  type Capability,
  type CapabilityEdit,
  type PersonRef,
} from "../../../shared/capabilities";
import type { CanonicalTeam } from "../../../shared/canonical-teams";
import type { Product, Vehicle } from "../../../shared/products";
import { asJson } from "./api";
import { OwnersField } from "./OwnersField";
import { PersonPicker } from "./PersonPicker";
import { ProductPicker } from "./ProductPicker";
import { VehiclePicker } from "./VehiclePicker";
import { FORM_HELP } from "./form-help";
import { TeamPicker } from "./TeamPicker";
import { Select } from "./fields";

export function EditCapability({
  current,
  teams,
  isAdmin,
  onSaved,
  onCancel,
  onTeamAdded,
}: {
  current: Capability;
  teams: CanonicalTeam[];
  isAdmin: boolean;
  onSaved: () => void;
  onCancel: () => void;
  /* a team added from this form is a new canonical team; the owner of the
     team list refreshes it (optional: a caller with a static list may skip) */
  onTeamAdded?: (team: CanonicalTeam) => void;
}): JSX.Element {
  const [teamList, setTeamList] = useState(teams);
  const [teamId, setTeamId] = useState(current.teamId);
  const [products, setProducts] = useState<Product[]>([]);
  const [productId, setProductId] = useState(current.productId ?? "");
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehicleId, setVehicleId] = useState(current.vehicleId ?? "");
  useEffect(() => {
    fetch("/api/intake/products")
      .then((res) => asJson<Product[]>(res))
      .then(setProducts)
      .catch(() => setProducts([]));
    fetch("/api/intake/vehicles")
      .then((res) => asJson<Vehicle[]>(res))
      .then(setVehicles)
      .catch(() => setVehicles([]));
  }, []);
  const [name, setName] = useState(current.name);
  const [description, setDescription] = useState(current.description ?? "");
  /* null on a row filed before the field existed — honestly not recorded,
     and left so unless someone picks a person */
  const [pointOfContact, setPointOfContact] = useState<PersonRef | null>(
    current.pointOfContact ?? null,
  );
  const [scope, setScope] = useState(current.scope);
  const [subStatus, setSubStatus] = useState(current.subStatus);
  const [neededBy, setNeededBy] = useState(current.neededBy ?? "");
  const [jamaLink, setJamaLink] = useState(current.jamaLink ?? "");
  const [dependencies, setDependencies] = useState(current.dependencies ?? "");
  /* null = not recorded. Setting this as an admin is what ROUTES the
     effort (UF6/UF10). */
  const [owners, setOwners] = useState<string[] | null>(current.owners ?? null);
  const [archived, setArchived] = useState(current.archived === true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const trimmedOrNull = (v: string): string | null => (v.trim() === "" ? null : v.trim());
    const next: CapabilityEdit = {
      teamId,
      name: name.trim(),
      description: trimmedOrNull(description),
      pointOfContact,
      productId: productId === "" ? null : productId,
      vehicleId: productId === "" || vehicleId === "" ? null : vehicleId,
      scope,
      subStatus,
      neededBy: neededBy === "" ? null : neededBy,
      jamaLink: trimmedOrNull(jamaLink),
      dependencies: trimmedOrNull(dependencies),
      owners,
      ...(isAdmin ? { archived } : {}),
    };
    const baseline: CapabilityEdit = {
      teamId: current.teamId,
      name: current.name,
      description: current.description,
      /* normalised like `next`: an absent key must not read as a change */
      pointOfContact: current.pointOfContact ?? null,
      productId: current.productId ?? null,
      vehicleId: current.vehicleId ?? null,
      scope: current.scope,
      subStatus: current.subStatus,
      neededBy: current.neededBy,
      jamaLink: current.jamaLink,
      dependencies: current.dependencies,
      owners: current.owners ?? null,
      ...(isAdmin ? { archived: current.archived === true } : {}),
    };
    const patch: CapabilityEdit = {};
    for (const key of Object.keys(next) as (keyof CapabilityEdit)[]) {
      const a = JSON.stringify(next[key] ?? null);
      const b = JSON.stringify(baseline[key] ?? null);
      if (a !== b) (patch as Record<string, unknown>)[key] = next[key];
    }
    if (Object.keys(patch).length === 0) {
      onCancel();
      return;
    }
    try {
      await asJson(
        await fetch(`/api/intake/capabilities/${current.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patch),
        }),
      );
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div data-edit-capability>
      <div className="capture-grid">
        <TeamPicker
          label="Team"
          teams={teamList}
          value={teamId}
          onChange={setTeamId}
          onAdded={(t) => {
            setTeamList((prev) => [...prev, t]);
            onTeamAdded?.(t);
          }}
          disabled={busy}
        />
        <ProductPicker
          label="Product (optional)"
          products={products}
          teams={teamList}
          value={productId}
          onChange={(id) => {
            setProductId(id);
            setVehicleId("");
          }}
          disabled={busy}
          help={FORM_HELP.product}
        />
        {productId !== "" && (
          <VehiclePicker
            label="Vehicle (optional)"
            productId={productId}
            vehicles={vehicles}
            value={vehicleId}
            onChange={setVehicleId}
            onAdded={(v) => setVehicles((prev) => [...prev, v])}
            disabled={busy}
            help={FORM_HELP.vehicle}
          />
        )}
        <label className="capture-field capture-wide">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="capture-field capture-wide">
          <span>What / why</span>
          <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <PersonPicker
          label="Point of contact"
          value={pointOfContact}
          onChange={setPointOfContact}
          missing="Not recorded — every effort names its lane lead."
          disabled={busy}
        />
        <Select label="Status" value={subStatus} options={SUB_STATUSES} onChange={setSubStatus} />
        <Select label="Affects" value={scope} options={SCOPES} onChange={setScope} />
        <label className="capture-field">
          <span>Needed by</span>
          <input type="date" value={neededBy} onChange={(e) => setNeededBy(e.target.value)} />
        </label>
        <label className="capture-field capture-wide">
          <span>Jama link</span>
          <input value={jamaLink} onChange={(e) => setJamaLink(e.target.value)} />
        </label>
        <label className="capture-field capture-wide">
          <span>Dependencies / needs from Platform</span>
          <textarea rows={2} value={dependencies} onChange={(e) => setDependencies(e.target.value)} />
        </label>
        {/* the routing path (OM5/UF6): an admin naming the owners here is
            what moves the effort out of "Nobody owns this yet" */}
        <OwnersField teams={teamList} value={owners} onChange={setOwners} />
        {isAdmin && (
          <div className="capture-field">
            <span>Curation</span>
            <div className="capture-checks">
              <label className={archived ? "capture-check on" : "capture-check"}>
                <input type="checkbox" checked={archived} onChange={() => setArchived(!archived)} />
                Archived
              </label>
            </div>
          </div>
        )}
      </div>
      {error !== null && <p className="capture-error">{error}</p>}
      <button className="capture-submit" disabled={busy} onClick={() => void save()}>
        {busy ? "Saving…" : "Save changes"}
      </button>
      <button className="capture-notdup" onClick={onCancel} disabled={busy}>
        Cancel
      </button>
    </div>
  );
}
